import { extendPromise } from '@scayle/storefront-nuxt'
import {
  computed,
  type MaybeRefOrGetter,
  readonly,
  type Ref,
  ref,
  toValue,
} from 'vue'
import type { RangeTuple } from '@scayle/storefront-product-listing'

import type { LocationQuery } from '#vue-router'
import { useToast, useTrackingEvents } from '~/composables'
import { useRoute, useRouter } from '#app/composables/router'
import { useI18n } from '#i18n'
import {
  createNewAttributeQuery,
  createNewBoolAttributeQuery,
  createNewPriceQuery,
  getClearedFilterQueryByKey,
  useAppliedFilters,
  useFiltersForListing,
  type UseFiltersForListingReturn,
} from '#storefront-product-listing'
import { createNewReductionQuery } from '#storefront-product-listing/utils/filters'

// Module-level cache to store filter instances by categoryId
// This avoids duplicate requests and doesn't require serialization
const filterDataCache = new Map<
  number,
  ReturnType<typeof useFiltersForListing>
>()

interface FilterOptions {
  immediate?: boolean
  keyPrefix?: string
}

interface FilterValue {
  name: string
  id: number
  productCount: number
  value: string
}

type UseFilterReturn = Awaited<UseFiltersForListingReturn> & {
  /**
   * Callback function that should be called by SFFilterSlideIn.vue when the filter slide-in is opened.
   * It tracks the filter flyout open event.
   */
  onSlideInOpen: () => void
  /**
   * Callback function that should be called by SFFilterSlideIn.vue when the filter slide-in is closed.
   * It tracks the filter flyout close event.
   */
  onSlideInClose: () => void
  /**
   * Applies a price filter to the current category.
   * @param prices - The prices to apply.
   */
  applyPriceFilter: (prices: RangeTuple) => Promise<void>
  /**
   * Applies a discount percentage filter (max_savings_percentage).
   * @param range - The range to apply.
   */
  applyReductionFilter: (range: RangeTuple) => Promise<void>
  /**
   * Applies a boolean filter to the current category.
   * @param slug - The name of the filter.
   * @param value - The value of the filter.
   */
  applyBooleanFilter: (slug: string, value: boolean) => Promise<void>
  /**
   * Applies an attribute filter to the current category.
   * @param slug - The name of the filter.
   * @param id - The id of the attribute.
   */
  applyAttributeFilter: (slug: string, id: number) => Promise<void>

  /**
   * Tracks the filter flyout event.
   * @param event - The event to track.
   * @param value - The value of the event.
   */
  trackFilterFlyout: (event: string, value: string) => void
  /**
   * Resets all filters.
   */
  resetFilters: () => Promise<void>
  /**
   * Resets the price filter.
   */
  resetPriceFilter: () => Promise<void>
  /**
   * Resets the reduction filter.
   */
  resetReductionFilter: () => Promise<void>
  /**
   * Resets a specific filter.
   * @param key - The name of the filter that should be reset.
   */
  resetFilter: (key: string) => Promise<void>
  /**
   * Indicates if all filters are cleared.
   * This flag will be automatically reset after 3 seconds.
   */
  areFiltersCleared: Readonly<Ref<boolean>>
}

/**
 * The `useFilter` composable centralizes the business logic for applying filters to the current category and tracking filter usage.
 *
 * @param currentCategoryId - The current category id.
 * @param options - The options for the filter.
 * @param options.immediate - Whether to fetch filters immediately.
 * @param options.keyPrefix - The prefix of the filter key.
 * @returns A {@link UseFilterReturn} object containing functions to apply filters and track filter usage.
 */
export function useFilter(
  currentCategoryId?: MaybeRefOrGetter<number | undefined>,
  options: FilterOptions = {},
): UseFilterReturn & Promise<UseFilterReturn> {
  const route = useRoute()
  const router = useRouter()
  const areFiltersCleared = ref(false)
  const areFiltersUpdated = ref(false)
  const categoryId = computed(
    () => Number(route.params?.id) || toValue(currentCategoryId),
  )

  const { appliedFiltersCount, appliedFilter } = useAppliedFilters(route)

  const sort = ref(route.query.sort)

  const params = computed(() => ({
    categoryId: categoryId.value,
    where: appliedFilter.value,
    includeSellableForFree: true,
  }))

  // Use computed categoryId for fetchingKey to avoid duplicate requests for the same categoryId
  const fetchingKey = computed(() => `filters-${categoryId.value}`)

  // Use a module-level cache to share filter data across components
  // This ensures only one useFiltersForListing call per categoryId
  const categoryIdValue = categoryId.value
  const cachedData = categoryIdValue
    ? filterDataCache.get(categoryIdValue)
    : undefined
  const filterData =
    cachedData ??
    (() => {
      const newFilterData = useFiltersForListing({
        params,
        fetchingKey: fetchingKey.value,
        fetchingOptions: options,
      })
      if (categoryIdValue) {
        filterDataCache.set(categoryIdValue, newFilterData)
      }
      return newFilterData
    })()

  const { trackFilterApply, trackFilterFlyout } = useTrackingEvents()
  const { t } = useI18n()

  const { show } = useToast()

  const applyAttributeFilter = async (slug: string, id: number) => {
    const filters = createNewAttributeQuery(route, appliedFilter.value, {
      slug,
      id,
    })

    const filterList = selectedFilterValues(filters)
    trackFilterApply(filterList)
    await applyFilters(filters)
  }

  const applyBooleanFilter = async (slug: string, value: boolean) => {
    const filters = createNewBoolAttributeQuery(route, appliedFilter.value, {
      slug,
      value,
    })

    trackFilterApply(value.toString())
    await applyFilters(filters)
  }

  const applyPriceFilter = async (prices: RangeTuple) => {
    const filters = createNewPriceQuery(route, appliedFilter.value, prices)

    trackFilterApply(prices.join(','))
    await applyFilters(filters)
  }

  const applyReductionFilter = async (reductions: RangeTuple) => {
    const filters = createNewReductionQuery(
      route,
      appliedFilter.value,
      reductions,
    )

    trackFilterApply(reductions.join(','))
    await applyFilters(filters)
  }

  const onSlideInOpen = () => trackFilterFlyout('open', 'true')

  const onSlideInClose = () => {
    trackFilterFlyout('close', 'true')
    const isSortUpdated = sort.value !== route.query.sort

    if (areFiltersUpdated.value && isSortUpdated) {
      show(t('filter.notification.filter_and_sort_updated'), {
        type: 'SUCCESS',
      })
      areFiltersUpdated.value = false
      sort.value = route.query.sort
    } else if (areFiltersUpdated.value) {
      show(t('filter.notification.filter_updated'), { type: 'SUCCESS' })
      areFiltersUpdated.value = false
    } else if (isSortUpdated) {
      show(t('filter.notification.sort_updated'), { type: 'SUCCESS' })
      sort.value = route.query.sort
    }
  }

  const resetPriceFilter = async () => {
    await applyFilters(getClearedFilterQueryByKey(route, 'prices'))
  }

  const resetReductionFilter = async () => {
    await applyFilters(getClearedFilterQueryByKey(route, 'savings_percentage'))
  }

  const resetFilter = async (key: string) => {
    await applyFilters(getClearedFilterQueryByKey(route, key))
  }

  const resetFilters = async () => {
    await applyFilters({})
    areFiltersCleared.value = true
    areFiltersUpdated.value = false

    setTimeout(() => {
      areFiltersCleared.value = false
    }, 3000)
  }

  const selectedFilterValues = (filter: LocationQuery) => {
    const activeFilterValueList = Object.entries(filter).flatMap(
      ([key, value]) => {
        const match = key.match(/^filters\[(.+?)\]$/)
        if (!match) {
          return []
        }

        const slug = match[1]
        const selectedIds = (value as string).split(',').map((id) => id.trim())
        const availableFilters = filterData.availableFilters.value
        const filter = availableFilters.find((f) => f.slug === slug)

        if (!filter || !filter.values) {
          return []
        }

        return filter.values.filter((val) => {
          if (!('id' in val)) {
            return false // or return
          }
          return selectedIds.includes(val.id.toString())
        })
      },
    ) as FilterValue[]
    return activeFilterValueList.map((val) => val.name).join(', ')
  }

  const applyFilters = async (filter?: LocationQuery, scrollToTop = true) => {
    if (!filter) {
      return
    }

    // Should not apply reset all filter if appliedFilter is empty
    if (!appliedFiltersCount.value && !Object.keys(filter).length) {
      return
    }
    const query = {
      sort: route.query.sort,
      'filters[term]': route.query['filters[term]'],
      ...filter,
    }

    if ('page' in query) {
      delete query.page
    }

    await router.push({ query })

    areFiltersUpdated.value = true

    if (scrollToTop) {
      window.scroll({ behavior: 'smooth', top: 0 })
    }
  }

  return extendPromise(filterData, {
    onSlideInOpen,
    onSlideInClose,
    applyPriceFilter,
    applyReductionFilter,
    applyBooleanFilter,
    applyAttributeFilter,
    trackFilterFlyout,
    resetFilters,
    resetPriceFilter,
    resetReductionFilter,
    resetFilter,
    areFiltersCleared: readonly(areFiltersCleared),
  })
}
